import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import InfoIcon from '@material-ui/icons/Info';
import './DeliveryManager.scss';
import useQuery from '@pluto/query-hook';
import {curry} from 'ramda';
import wrap from 'wraptile';
import humanizeString from 'humanize-string';
import {getOptsFromQuery, handleSelect} from '@pluto/select';
import {useHistory} from 'react-router';
import {
    ColumnType,
    getInitialPage,
    getInitialRowsPerPage,
    getInitialSort,
    Sort,
    SortingOptionsValues,
} from '@pluto/table';
import {DateRangeState, getInitialRanges} from '@pluto/daterange-picker';
import {handleDebounce, handleDebouncedCallback} from '@pluto/qc-debounce';
import {setQuery} from '@pluto/set-history-query';
import {baseRoutes} from 'App/AppRouter/routes';
import {useDebouncedCallback} from 'use-debounce';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import ReplayIcon from '@material-ui/icons/Replay';
import CancelIcon from '@material-ui/icons/Cancel';
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import {makeHandleFilters} from '@pluto/qc-handle-filters';
import {handleAutocompleteChange} from '@pluto/autocomplete';
import {handleDateRangeChange} from '@pluto/qc-handle-daterange';
import Table from '@pluto/table/TableContainer';
import {handleQcManagerList} from '@pluto/qc-paging';
import {handleMount} from '@pluto/qc-mount';
import {Section, SummaryContainer as DeliverySummary} from '@pluto/common/summary';
import {handlePreselect} from '@pluto/qc-preselect';
import {QCSpecificFilters} from '../QCFilters/QCFiltersType';
import {
    FilterHandlers,
    FiltersKeys,
    HandleChangeInputType,
    HandlePreselectChange,
    QCJobItem,
} from '../QCManager/QCManagerTypes';
import DeliveryManagerFilters from './DeliveryManagerFilter/DeliveryFiltersConnected';
import {
    TableRowType,
    QueryParams,
    DeliverySectionTitles,
    DeliveryManagerState,
    DeliveryStatusEnum,
} from './DeliveryManagerTypes';

export interface DeliveryManagerContainerProps {
    setQCJobs: (params: QueryParams) => void;
    resetQCJobs: () => void;
    getQCJobsSummary: () => void;
    retryTask: (id: number) => void;
    cancelTask: (id: number) => void;
    isLoading: boolean;
    total: number;
    rows: QCJobItem[];
    deliveryManager: DeliveryManagerState;
}

const SCORE_BOARD_SECTIONS: Section[] = [
    {
        status: [DeliveryStatusEnum.InProgress],
        title: DeliverySectionTitles.InProgress,
    },
    {
        status: [DeliveryStatusEnum.Pending],
        title: DeliverySectionTitles.Pending,
    },
    {
        status: [DeliveryStatusEnum.Failed],
        title: DeliverySectionTitles.Failed,
    },
    {
        status: [DeliveryStatusEnum.Complete],
        title: DeliverySectionTitles.CompleteTwo,
        complete: true,
    },
];

export const renderDeliveryManifestLink = ({id, status, contentType}: TableRowType) => {
    return (status.toLowerCase() === 'complete' && (contentType === 'film' || contentType === 'episode')) ? (
        <Link to={`/delivery-manager/manifest/${id}`}>
            <InfoIcon
                style={{
                    color: '#fff',
                }}
            />
        </Link>
    ) : null;
};

export const renderActionIcons = curry(
    (
        execute: (filters: QueryParams, id: number, func: (id: number) => void) => void,
        retryTask: (id: number) => void,
        cancelTask: (id: number) => void,
        filters: QueryParams,
        {status, id}: TableRowType
    ) => {
        return status.toLowerCase() === 'failed' ? (
            <div className="icons-container">
                <span role="button" onClick={execute(filters, id, retryTask) as any}>
                    <ReplayIcon />
                </span>
                <span role="button" onClick={execute(filters, id, cancelTask) as any}>
                    <CancelIcon />
                </span>
            </div>
        ) : null;
    }
);

export const renderContentType = ({contentType}: TableRowType) =>
    humanizeString(contentType as string);

export const renderStatus = ({status, id, seriesTitle}: TableRowType) => {
    const humanStatus = humanizeString(status);
    switch (humanStatus) {
        case 'Pending':
            return (
                <Tooltip title={seriesTitle} key={id}>
                    <Typography className="status-orange">{humanStatus}</Typography>
                </Tooltip>
            );

        case 'Complete':
            return <Typography className="status-green">{humanStatus}</Typography>;

        case 'In progress':
            return <Typography className="status-white">{humanStatus}</Typography>;

        case 'Failed':
            return <Typography className="status-red">{humanStatus}</Typography>;

        default:
            return <Typography className="status-grey">{humanStatus}</Typography>;
    }
};

export const execute = wrap(
    (
        filters: QueryParams,
        id: number,
        task: (id: number, filters: QueryParams) => void
    ) => task(id, filters)
);

export const getInitialDeliveryManagerSort = (query: URLSearchParams): Sort => {
    const initialSort = getInitialSort(query);

    if (initialSort.sortBy) {
        return initialSort;
    }

    return {
        sortBy: 'deliveryJob.startDate',
        sortOrder: SortingOptionsValues.desc,
    };
};

export const renderTitle = ({displayTitle}: TableRowType) => displayTitle;

export const DeliveryManagerContainer = (props: DeliveryManagerContainerProps) => {
    const {
        setQCJobs,
        resetQCJobs,
        retryTask,
        cancelTask,
        deliveryManager,
        getQCJobsSummary,
    } = props;

    const {isLoading, total, rows} = deliveryManager;

    const query = useQuery();
    const getFromQuery = getOptsFromQuery(query);
    const history = useHistory();
    const initialRowsPerPage = getInitialRowsPerPage(query);
    const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const DEBOUNCE_TIME = 700;
    const [page, setPage] = useState<number>(initialPage);
    const initialSort = getInitialDeliveryManagerSort(query);
    const [sort, setSort] = useState<Sort>(initialSort);
    const [title, setTitle] = useState<string>(query.get('title') || '');
    const initialDueDate = getInitialRanges(query, 'dateStart', 'dateEnd');
    const initialFinishDate = getInitialRanges(query, 'finishDateStart', 'finishDateEnd');
    const initialStartDate = getInitialRanges(query, 'startDateStart', 'startDateEnd');
    const [dueDates, setDueDates] = useState<DateRangeState>(initialDueDate);
    const [finishDates, setFinishDates] = useState<DateRangeState>(initialFinishDate);
    const [startDates, setStartDates] = useState<DateRangeState>(initialStartDate);
    const [filters, setFilters] = useState<QCSpecificFilters>({
        qcJobType: query.get('qcJobType') || '',
        contentPartnerId: getFromQuery('contentPartnerId'),
        contentItemType: getFromQuery('contentItemType'),
        qcJobAssigneeId: getFromQuery('qcJobAssigneeId'),
        qcJobStatus: getFromQuery('qcJobStatus'),
    });

    const debouncedCallback = handleDebouncedCallback({
        ...filters,
        ...sort,
        title,
        limit: rowsPerPage,
        offset: 0,
        setQCJobs,
        setPage,
        setQuery,
        history,
        route: baseRoutes.deliveryManager,
    });
    const [debouncedFunction] = useDebouncedCallback(debouncedCallback, DEBOUNCE_TIME);

    const onDebouncedSearch: HandleChangeInputType = handleDebounce({
        debouncedFunction,
        setTitle,
    });

    const filterHandlers: FilterHandlers = {
        ...sort,
        setFilters,
        setPage,
        setQuery,
        history,
        setQCJobs: setQCJobs as any,
        filters,
        rowsPerPage,
        title,
        route: baseRoutes.deliveryManager,
        setDueDates,
        setFinishDates,
        dueDates,
        finishDates,
        startDates,
        setStartDates,
    };
    const params = {
        ...filters,
        ...sort,
        title,
        finishDates,
        dates: dueDates,
        offset: rowsPerPage * page,
        limit: rowsPerPage,
    };

    const onPreselectClick: HandlePreselectChange = handlePreselect(filterHandlers);

    const handleFilters = makeHandleFilters(filterHandlers);
    const handleTypeChange = handleFilters(FiltersKeys.Type);

    const handleContentPartnersChange = handleAutocompleteChange({
        ...filterHandlers,
        key: FiltersKeys.ContentPartnerId,
    });

    const handleContentTypesChange = handleAutocompleteChange({
        ...filterHandlers,
        key: FiltersKeys.ContentType,
    });

    const handleStatusChange = handleSelect({
        ...filterHandlers,
        key: FiltersKeys.Status,
    });

    const onFinishDateRangeSelect = handleDateRangeChange(filterHandlers, 'finishDate');
    const onStartDateRangeSelect = handleDateRangeChange(filterHandlers, 'startDate');
    const columns: ColumnType[] = [
        {
            dataKey: 'title',
            title: 'Title',
            render: renderTitle,
        },
        {
            dataKey: 'startDate',
            title: 'Started',
            sortable: true,
        },
        {
            dataKey: 'finishDate',
            title: 'Finished',
        },
        {
            dataKey: 'contentPartner',
            title: 'Content partner',
        },
        {
            dataKey: 'contentType',
            title: 'Content type',
            render: renderContentType,
        },
        {
            dataKey: 'status',
            title: 'Delivery status',
            render: renderStatus,
            width: 15,
        },
        {
            dataKey: 'manifest',
            title: '',
            render: renderDeliveryManifestLink,
            width: 1,
        },
        {
            dataKey: '',
            title: '',
            render: renderActionIcons(execute, retryTask, cancelTask, params as any),
        },
    ];

    const onPaging = handleQcManagerList(params, {
        setQCJobs: setQCJobs as any,
        setQuery,
        history,
        route: baseRoutes.deliveryManager,
    });

    const onMount = handleMount({
        setQCJobs,
        resetQCJobs,
        getQCJobsSummary,
        setRowsPerPage,
        setPage,
        params,
        query,
    });

    useEffect(onMount, []);

    if (isLoading) {
        return <CircularProgress />;
    }
    return (
        <div className="delivery-manager-container">
            <DeliverySummary
                status={filters.qcJobStatus}
                setFilter={onPreselectClick}
                setDates={setFinishDates}
                sections={SCORE_BOARD_SECTIONS}
                state={deliveryManager}
            />
            <DeliveryManagerFilters
                filters={filters}
                title={title}
                handleTitleChange={onDebouncedSearch}
                handleTypeChange={handleTypeChange}
                handleContentPartnersChange={handleContentPartnersChange}
                handleContentTypesChange={handleContentTypesChange}
                handleStatusChange={handleStatusChange}
                onFinishDateRangeSelect={onFinishDateRangeSelect}
                onStartDateRangeSelect={onStartDateRangeSelect}
                finishDates={finishDates}
                startDates={startDates}
            />
            <Table
                data={rows}
                columns={columns}
                setSort={setSort}
                sort={sort}
                total={total}
                onChangePageHandler={onPaging}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
            >
                <div className="qc-work-queue-control-buttons__wrapper">
                    <Typography
                        variant="h5"
                        className="control-buttons__elem control-buttons__elem_title"
                    >
                        Delivery queue
                    </Typography>
                    <div className="control-buttons__elem control-buttons__items">
                        {total} items
                    </div>
                </div>
            </Table>
        </div>
    );
};
